import { Component, EventEmitter, Input, OnInit, Output, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { ImageHydrationProgress, ImageHydrationService } from "../../../../../../core/sync/image-sync/image-hydration.service";
import { CommunityLotDetail } from "../../models/CommunityLotDetail";
import { LotPrimaryIdentifier } from "../../../../../../core/feature-flags/models/AdminConfigDto";


@Component({
  standalone: true,
  selector: "app-lot-card",
  imports: [CommonModule],
  templateUrl: "./lot-card.component.html",
  styleUrl: "./lot-card.component.scss"
})
export class LotCardComponent implements OnInit {
  @Input({ required: true }) lotItem: CommunityLotDetail;
  @Input({ required: true }) communityId: number;
  @Input({ required: true }) lotPrimaryIdentifier: LotPrimaryIdentifier;
  @Output() archiveRequested = new EventEmitter<number>();

  lotBlockNumber: string;
  lotAddress: string;
  imageHydrationProgress = signal<Record<string, ImageHydrationProgress>>({});

  overallImageProgress = 0;
  isLoadingImages = false;

  /** Horizontal swipe tracking */
  private swipeStartX = 0;
  private swipeStartY = 0;
  private readonly SWIPE_THRESHOLD = 60;
  swipeOffset = 0;
  isSwipedOpen = false;

  constructor(private router: Router, private imageHydrationService: ImageHydrationService) {
    this.imageHydrationService.progressUpdated.pipe(takeUntilDestroyed()).subscribe(({ jobId, progress }) => {
      if (this.lotItem.jobIds?.includes(jobId)) {
        this.updateImageProgress(jobId, progress);
      }
    });
  }

  ngOnInit(): void {
    this.FormatAddress();

    for (const jobId of this.lotItem.jobIds ?? []) {
      const progress = this.imageHydrationService.jobProgress[jobId];
      if (progress) {
        this.updateImageProgress(jobId, progress);
      }
    }
  }

  getLotUrl(): string {
    return `/cm/all-my-lots/community/${this.communityId}/lot/${this.lotItem.id}/summary`;
  }

  OnCardClick(event?: MouseEvent) {
    event?.preventDefault();
    if (this.isLoadingImages || this.isSwipedOpen) {
      if (this.isSwipedOpen) {
        this.closeSwipe();
      }
      return;
    }

    this.router.navigate(["/cm/all-my-lots/community", this.communityId, "lot", this.lotItem.id, "summary"], {
      state: { fromAllLots: true }
    });
  }

  onTouchStart(event: TouchEvent): void {
    if (this.isLoadingImages) {
      return;
    }

    const touch = event.touches[0];
    this.swipeStartX = touch.clientX;
    this.swipeStartY = touch.clientY;
  }

  onTouchMove(event: TouchEvent): void {
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.swipeStartX;
    const deltaY = Math.abs(touch.clientY - this.swipeStartY);

    // Only track horizontal swipes (not vertical scrolls)
    if (deltaY > Math.abs(deltaX)) {
      return;
    }

    if (deltaX < 0) {
      this.swipeOffset = Math.max(deltaX, -80);
      event.preventDefault?.();
    } else if (this.isSwipedOpen) {
      this.swipeOffset = Math.min(-80 + deltaX, 0);
      event.preventDefault?.();
    }
  }

  onTouchEnd(): void {
    if (this.swipeOffset < -this.SWIPE_THRESHOLD) {
      this.isSwipedOpen = true;
      this.swipeOffset = -80;
    } else {
      this.closeSwipe();
    }
  }

  closeSwipe(): void {
    this.isSwipedOpen = false;
    this.swipeOffset = 0;
  }

  onArchiveClick(event: Event): void {
    event.stopPropagation();
    this.archiveRequested.emit(this.lotItem.id);
    this.closeSwipe();
  }

  FormatAddress() {
    const source = this.lotItem?.address ?? "";
    const number = source.match(/^(.*?)\s*-/);
    const address = source.match(/-\s*(.*)$/);
    this.lotBlockNumber = number ? number[1].trim() : source.trim();
    this.lotAddress = address ? address[1].trim() : source.trim();
  }

  get isAddressPrimary(): boolean {
    return this.lotPrimaryIdentifier === LotPrimaryIdentifier.Address;
  }

  get primaryDisplayValue(): string {
    return this.isAddressPrimary ? this.lotAddress : this.lotBlockNumber;
  }

  get secondaryDisplayValue(): string {
    return this.isAddressPrimary ? this.lotBlockNumber : this.lotAddress;
  }

  private updateImageProgress(jobId: number, progress: ImageHydrationProgress) {
    this.imageHydrationProgress.update(current => {
      current[jobId] = progress;
      return current;
    });
    const totalProgress = this.imageHydrationProgress();
    const total = Object.keys(totalProgress).length;
    if (total === 0) {
      this.overallImageProgress = 0;
      return;
    }
    const completed = Object.values(totalProgress).reduce((sum, p) => sum + p.percentage, 0);
    this.overallImageProgress = Math.round(completed / total);
    this.isLoadingImages = this.overallImageProgress > 0 && this.overallImageProgress < 100;
  }
}
